import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { getMockedResponseForOnboardingModuleConfig } from 'bundles/onboarding-common/mocks/mockModuleConfig';
import GetOnboardingModuleConfig from 'bundles/onboarding-common/queries/GetOnboardingModuleConfig.graphql';
import type {
  GetOnboardingModuleConfigQuery,
  GetOnboardingModuleConfigQueryVariables,
} from 'bundles/onboarding-common/queries/__generated__/GetOnboardingModuleConfig';
import type { InferredRole, ModularOnboardingEntrypoint } from 'bundles/onboarding-common/types';

export type OnboardingModuleConfigRequest = {
  entrypoint: ModularOnboardingEntrypoint;
  inferredRole?: InferredRole;
};

const useGetOnboardingModuleConfig = (configInput: OnboardingModuleConfigRequest, skip: boolean = false) => {
  // TODO: TEMPORARY - Remove this mock data approach after launch
  // This query parameter approach is only for testing purposes and should be removed in production
  // Check if we should use mock data based on query parameter
  const shouldUseMockData = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('useMockData') === 'true';
  }, []);

  // Use mock data if query parameter is present
  const mockData = useMemo(() => {
    return getMockedResponseForOnboardingModuleConfig(configInput);
  }, [configInput]);

  const { data, loading } = useQuery<GetOnboardingModuleConfigQuery, GetOnboardingModuleConfigQueryVariables>(
    GetOnboardingModuleConfig,
    {
      variables: { entrypoint: configInput.entrypoint, inferredRoleId: configInput.inferredRole?.id },
      skip: skip || shouldUseMockData, // Skip GraphQL query if using mock data
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  if (shouldUseMockData) return { ...mockData, loading: false };

  return {
    displayModule: data?.LearnerOnboarding?.getOnboardingModuleConfig?.displayModule,
    module: data?.LearnerOnboarding?.getOnboardingModuleConfig?.module,
    loading,
  };
};

export default useGetOnboardingModuleConfig;
